import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  Typography,
  FormControlLabel,
  Paper,
  Button,
  Chip,
  Divider,
  Tooltip,
  IconButton
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LockIcon from "@mui/icons-material/Lock";
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import WarningIcon from "@mui/icons-material/Warning";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchUserAssets,
  setSelectedAsset
} from "../../features/userAssets/userAssetsSlice";
import {
  setSelectedDatabase,
  selectDatabase,
  setSelectedMetadata
} from "../../features/settings/settingsSlice";
import { getLicenseStatus } from "../../features/userAssets/helpers";


const QueryInfo = ({
  errors,
  queryInfo,
  handleQueryInfoChange,
  handleNext,
  userId,
  isEditMode = false
}) => {
  // For tags functionality
  const [tagInput, setTagInput] = useState("");
  const [userHasSelected, setUserHasSelected] = useState(false);
  const dispatch = useDispatch();
  const { userAssets, loading } = useSelector((store) => store.userAssets);
  const selectedDatabase = useSelector(selectDatabase);

  useEffect(() => {
    // If we have userAssets and a selectedDatabase (from existing query),
    // but no selectedAsset, then set the selectedAsset
    if (userAssets && userAssets.length > 0 && selectedDatabase && (!userHasSelected || isEditMode)) {
      const selectedAssetObject = userAssets.find(asset => asset.AssetName === selectedDatabase);
      if (selectedAssetObject) {
        dispatch(setSelectedAsset(selectedAssetObject));
        dispatch(setSelectedMetadata(selectedAssetObject?.Metadata));
      }
    }
  }, [userAssets, selectedDatabase, userHasSelected, dispatch, isEditMode]);

  const handleDatabaseChange = (e) => {
    const selectedAssetName = e.target.value;
    setUserHasSelected(true);
    const selectedAssetObject = userAssets.find(asset => asset.AssetName === selectedAssetName);
    if (selectedAssetObject) {
      // Set the selected asset in the userAssets slice
      dispatch(setSelectedAsset(selectedAssetObject));

      // Set the selected database in the settings slice
      dispatch(setSelectedDatabase(selectedAssetName));

      // Set metadata from the assetObject
      dispatch(setSelectedMetadata(selectedAssetObject?.Metadata))
    }
  };

  const handleTagInputChange = (e) => {
    setTagInput(e.target.value);
  };

  const handleTagKeyDown = (e) => {
    if (e.key === "Enter" && tagInput.trim() !== "") {
      e.preventDefault();
      const newTag = tagInput.trim();
      const currentTags = queryInfo.tags
        ? queryInfo.tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t)
        : [];

      if (!currentTags.includes(newTag)) {
        const updatedTags = [...currentTags, newTag].join(", ");
        const mockEvent = {
          target: {
            value: updatedTags,
          },
        };
        handleQueryInfoChange({ target: { value: updatedTags } }, "tags");
      }

      setTagInput("");
    }
  };

  const handleRefreshDatabases = () => {
    if (!loading && userId) {
      dispatch(fetchUserAssets(userId));
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    const currentTags = queryInfo.tags.split(",").map(t => t.trim()).filter(t => t);
    const updatedTags = currentTags.filter(tag => tag !== tagToDelete).join(", ");

    const mockEvent = {
      target: {
        value: updatedTags
      }
    };

    handleQueryInfoChange(mockEvent, "tags");
  };

  // Get current tags as array
  const currentTags = queryInfo.tags
    ? queryInfo.tags.split(",").map(t => t.trim()).filter(t => t !== "")
    : [];

  const renderDatabaseOption = (asset) => {
    const licenseStatus = getLicenseStatus(asset);
    const isDisabled = licenseStatus.status === 'expired';

    const getStatusConfig = () => {
      switch (licenseStatus.status) {
        case 'expired':
          return {
            chipColor: 'error',
            chipLabel: 'Expired',
            icon: <LockIcon sx={{ fontSize: 16 }} />,
            iconColor: 'error.main',
            tooltipText: `License expired ${Math.abs(licenseStatus.daysUntilExpiry)} days ago`,
            textColor: 'text.disabled'
          };
        case 'expiring_soon':
          return {
            chipColor: 'warning',
            chipLabel: `${licenseStatus.daysUntilExpiry} days left`,
            icon: <WarningIcon sx={{ fontSize: 16 }} />,
            iconColor: 'warning.main',
            tooltipText: `License expires in ${licenseStatus.daysUntilExpiry} days`,
            textColor: 'text.primary'
          };
        default:
          return {
            chipColor: 'success',
            chipLabel: 'Active',
            icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
            iconColor: 'success.main',
            tooltipText: `License expires on ${new Date(asset.ExpirationDate).toLocaleDateString()}`,
            textColor: 'text.primary'
          };
      }
    };

    const statusConfig = getStatusConfig();

    return (
      <MenuItem
        key={asset.AssetName}
        value={asset.AssetName}
        disabled={isDisabled}
        sx={{
          py: 2,
          px: 2,
          opacity: isDisabled ? 0.5 : 1,
          '&:hover': {
            backgroundColor: isDisabled ? 'transparent' : 'action.hover'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          gap: 1.5
        }}>
          {/* Status Icon */}
          <Box sx={{
            color: statusConfig.iconColor,
            display: 'flex',
            alignItems: 'center'
          }}>
            {statusConfig.icon}
          </Box>

          {/* Database Info */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: statusConfig.textColor,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {asset.AssetName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {asset.AccountName}
            </Typography>
          </Box>

          {/* Status Chip */}
          <Tooltip title={statusConfig.tooltipText} arrow>
            <Chip
              label={statusConfig.chipLabel}
              size="small"
              color={statusConfig.chipColor}
              variant={licenseStatus.status === 'active' ? 'filled' : 'outlined'}
              sx={{
                fontSize: '0.7rem',
                height: '20px',
                minWidth: isDisabled ? '60px' : 'auto',
                '& .MuiChip-label': {
                  px: 1
                }
              }}
            />
          </Tooltip>
        </Box>
      </MenuItem>
    );
  };

  return (
    <Box sx={{ width: "100%", maxWidth: "1200px", mx: "auto", mt: 1 }}>
      <Paper
        elevation={3}
        sx={{
          overflow: "hidden",
          borderRadius: 2
        }}
      >
        {/* Header with gradient */}
        <Box
          sx={{
            padding: '20px',
            background: "linear-gradient(90deg, #04abe6 0%, #00b0ff 100%)",
            color: "white",
            position: "relative"
          }}
        >
          <Typography variant="h5" component="h2" fontWeight={600} mb={0.5} sx={{ color: "white" }}>
            {isEditMode ? "Edit Query" : "Create a New Query"}
          </Typography>
          <Typography variant="body2" sx={{ maxWidth: "md", opacity: 0.9, color: "white" }}>
            {isEditMode
              ? "Update the name or description of your query"
              : "Fill in the details to create your query"}
          </Typography>

          {/* Decorative search icon */}
          <Box sx={{ position: "absolute", right: 24, top: 24, opacity: 0.2 }}>
            <SearchIcon sx={{ fontSize: 48 }} />
          </Box>
        </Box>

        {/* Form Content */}
        <Box sx={{ p: '20px' }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Query Title */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} mb={1}>
                Query Title*
              </Typography>
              <TextField
                fullWidth
                value={queryInfo.title}
                onChange={(e) => handleQueryInfoChange(e, "title")}
                placeholder="E.g., Cardiac MRI T1 mapping protocol"
                error={errors.title.show}
                helperText={errors.title.show ? errors.title.message : "Choose a descriptive title that others can easily understand"}
                size="small"
                sx={{ "& .MuiFormHelperText-root": { mt: 1 } }}
              />
            </Box>

            {/* Tags */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} mb={1}>
                Tags
              </Typography>
              <TextField
                fullWidth
                value={tagInput}
                onChange={handleTagInputChange}
                onKeyDown={handleTagKeyDown}
                placeholder="E.g., cardiology, imaging, protocol (press Enter to add)"
                size="small"
                helperText="Add relevant tags to help others find your query"
                sx={{ "& .MuiFormHelperText-root": { mt: 1 } }}
              />

              {/* Tag chips */}
              {currentTags.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {currentTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      onDelete={() => handleDeleteTag(tag)}
                      sx={{
                        bgcolor: "#1976d2",
                        color: "white",
                        "& .MuiChip-deleteIcon": {
                          color: "white",
                          "&:hover": {
                            color: "#f8f8f8"
                          }
                        }
                      }}
                    />
                  ))}
                </Box>
              )}
            </Box>

            {/* Description */}
            <Box>
              <Typography variant="subtitle2" fontWeight={500} mb={1}>
                Query Description*
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={queryInfo.desc}
                onChange={(e) => handleQueryInfoChange(e, "desc")}
                placeholder="Provide a clear description of what information you're seeking and why it's important..."
                helperText="Be specific to help others understand your needs and context"
                sx={{ "& .MuiFormHelperText-root": { mt: 1 } }}
              />
            </Box>

            {/* Database */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={500}>
                  Database*
                </Typography>
                {isEditMode && (
                  <Tooltip title="Database cannot be changed for existing queries">
                    <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Select
                  fullWidth
                  value={userHasSelected || isEditMode ? selectedDatabase || "" : ""}
                  onChange={handleDatabaseChange}
                  displayEmpty
                  size="small"
                  disabled={isEditMode}
                  error={errors.database.show}
                  sx={{
                    "& .MuiSelect-select": {
                      p: "10.5px 14px",
                    }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: 200,
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                        '& .MuiList-root': {
                          py: 1
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="" disabled>
                    <Typography variant="body2" color="text.secondary">
                      Select a database
                    </Typography>
                  </MenuItem>
                  {loading ? (
                    <MenuItem value="" >
                      <Typography variant="body2">Loading databases...</Typography>
                    </MenuItem>
                  ) : userAssets && userAssets.length > 0 ? (
                    userAssets.map((asset) => renderDatabaseOption(asset))
                  ) : (
                    <MenuItem value="" disabled>
                      <Typography variant="body2" color="text.secondary">
                        No databases available
                      </Typography>
                    </MenuItem>
                  )}
                </Select>
                {/* Refresh databases */}
                <Tooltip title={isEditMode ? "Cannot change database" : loading ? "Refreshing..." : "Refresh databases"}>
                  <span style={{ display: "inline-block" }}>
                    <IconButton
                      size="small"
                      onClick={handleRefreshDatabases}
                      disabled={loading || !userId || isEditMode}
                      aria-label="Refresh databases"
                      sx={{
                        "@keyframes spin": {
                          from: { transform: "rotate(0deg)" },
                          to: { transform: "rotate(360deg)" }
                        },
                        "& .MuiSvgIcon-root": loading ? { animation: "spin 1s linear infinite" } : undefined
                      }}
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>

              <Typography
                variant="caption"
                color={errors.database.show ? "error.main" : "text.secondary"}
                sx={{ display: "block", mt: 1 }}
              >
                {errors.database.show
                  ? errors.database.message
                  : isEditMode
                    ? "Database cannot be changed for existing queries"
                    : "Choose the most relevant database for your query"}
              </Typography>
            </Box>

            {/* Sharing */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={queryInfo.isShareable}
                  onChange={(e) => handleQueryInfoChange(e, "isShareable")}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  Share this query anonymously with other users
                </Typography>
              }
            />

            <Divider sx={{ my: 1 }} />

            {/* Footer */}
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mt: 1 }}>

              <Button
                variant="contained"
                onClick={handleNext}
                sx={{ px: 3, backgroundColor: '#00b0ff' }}
              >
                Begin
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default QueryInfo;
